import React from "react";
import { Box, Button, ToggleButton, ToggleButtonGroup } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import MapIcon from "@mui/icons-material/Map";
import ViewListIcon from "@mui/icons-material/ViewList";
import { useMessage } from "../i18n/locale";

export function FilterButton({
  count = 0,
  label,
  compactOnMobile = false,
  sx,
  ...buttonProps
}) {
  const showCount = Number(count) > 0;
  const defaultLabel = useMessage("filters");
  const displayLabel = label ?? defaultLabel;

  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<TuneIcon />}
      sx={{
        flexShrink: 0,
        minHeight: 34,
        whiteSpace: "nowrap",
        ...(compactOnMobile && {
          minWidth: { xs: 40, sm: "auto" },
          width: { xs: 40, sm: "auto" },
          px: { xs: 0, sm: 2.25 },
          "& .MuiButton-startIcon": { m: { xs: 0, sm: "0 8px 0 -4px" } },
        }),
        ...sx,
      }}
      {...buttonProps}
    >
      <Box component="span" sx={{ display: compactOnMobile ? { xs: "none", sm: "inline" } : "inline" }}>
        {displayLabel}
      </Box>
      {showCount && (
        <Box
          component="span"
          aria-label={`${count}`}
          sx={{
            display: "inline-grid",
            placeItems: "center",
            minWidth: 18,
            height: 18,
            px: 0.5,
            ml: compactOnMobile ? { xs: 0, sm: 0.75 } : 0.75,
            borderRadius: 999,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            fontSize: 11,
            fontWeight: 800,
            lineHeight: 1,
            ...(compactOnMobile && {
              position: { xs: "absolute", sm: "static" },
              insetInlineEnd: { xs: 2, sm: "auto" },
              insetBlockStart: { xs: 2, sm: "auto" },
              minWidth: { xs: 15, sm: 18 },
              height: { xs: 15, sm: 18 },
              px: { xs: 0.25, sm: 0.5 },
              fontSize: { xs: 9, sm: 11 },
            }),
          }}
        >
          {count}
        </Box>
      )}
    </Button>
  );
}

export function ViewModeToggle({
  mapMode,
  onChange,
  listLabel,
  mapLabel,
  sx,
}) {
  const defaultListLabel = useMessage("listView");
  const defaultMapLabel = useMessage("mapView");
  const displayListLabel = listLabel ?? defaultListLabel;
  const displayMapLabel = mapLabel ?? defaultMapLabel;

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={mapMode ? "map" : "list"}
      onChange={(_, value) => value && onChange?.(value === "map")}
      aria-label={`${displayListLabel} / ${displayMapLabel}`}
      sx={{
        flexShrink: 0,
        "& .MuiToggleButton-root": {
          minHeight: 34,
          px: 1.5,
          gap: 0.75,
          borderColor: "divider",
          color: "text.secondary",
          fontWeight: 700,
          textTransform: "none",
          whiteSpace: "nowrap",
          "&.Mui-selected": {
            bgcolor: "primary.main",
            color: "primary.contrastText",
            "&:hover": { bgcolor: "primary.dark" },
          },
        },
        "& .MuiSvgIcon-root": { fontSize: 18 },
        ...sx,
      }}
    >
      <ToggleButton value="list" aria-label={displayListLabel}>
        <ViewListIcon />
        {displayListLabel}
      </ToggleButton>
      <ToggleButton value="map" aria-label={displayMapLabel}>
        <MapIcon />
        {displayMapLabel}
      </ToggleButton>
    </ToggleButtonGroup>
  );
}
